/**
 * ConfirmDialog — wiederverwendbare Bestätigung mit Loading-State.
 *
 * Ersetzt:
 *  - `window.confirm(...)` Aufrufe in Staff-Pages
 *  - Custom-ConfirmModal in customer-portal/src/components/ui
 *
 * Beispiel:
 *   <ConfirmDialog
 *     isOpen={!!toDelete}
 *     title="Token wirklich widerrufen?"
 *     message={<>Token <strong>{toDelete?.description}</strong> wird sofort ungültig.</>}
 *     variant="danger"
 *     confirmLabel="Widerrufen"
 *     loading={mutation.loading}
 *     onConfirm={handleDelete}
 *     onCancel={() => setToDelete(null)}
 *   />
 */

import type { ReactNode } from 'react'
import { Modal } from '../Modal'
import './ConfirmDialog.css'

export type ConfirmVariant = 'default' | 'danger' | 'warning' | 'info'

export interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmVariant
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  variant = 'default',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const variantClass = `confirm-btn-${variant}`

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? () => {} : onCancel}
      title={title}
      size="sm"
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
      footer={
        <>
          <button
            type="button"
            className="confirm-btn confirm-btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`confirm-btn ${variantClass}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? '…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="confirm-message">{message}</div>
    </Modal>
  )
}

export default ConfirmDialog
